<template>
  <v-card>
    <v-toolbar dense card class="elevation-2">
      <div class="text-capitalize beez-bold">Rule | Audit Reports</div>
      <v-spacer />
      <v-tooltip bottom>
        <v-btn slot="activator" flat icon @click="refresh()">
          <v-icon>cached</v-icon>
        </v-btn>
        <span>Refresh</span>
      </v-tooltip>
    </v-toolbar>
    <v-card-title>
      <v-list-tile-avatar>
        <v-icon>search</v-icon>
      </v-list-tile-avatar>
      <v-list-tile-content>
        <v-list-tile-sub-title>
          <v-text-field
            color="secondary"
            label="Search"
            v-model="search"
            xs5
            @change="searchTasks"
          >
          </v-text-field>
        </v-list-tile-sub-title>
      </v-list-tile-content>
      <v-tooltip bottom>
        <v-btn slot="activator" flat icon @click="clearSearch">
          <v-icon>clear</v-icon>
        </v-btn>
        <span>Clear search</span>
      </v-tooltip>
    </v-card-title>
    <v-card-text>
      <v-layout>
        <v-flex xs12 md6>
          <v-menu
            v-model="fromDateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="computedFromDate"
                label="Rules changed from"
                prepend-icon="event"
                readonly
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="fromDate"
              :max="toDate"
              @input="fromDateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-flex>
        <v-flex xs12 md6>
          <v-menu
            v-model="toDateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="computedToDate"
                label="Rules changed till"
                prepend-icon="event"
                readonly
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="toDate"
              :min="fromDate"
              @input="toDateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-flex>
      </v-layout>
      <v-data-table
        :headers="headers"
        :items="ruleItems"
        :loading="taskLoader"
        item-key="revisionNumber"
        :total-items="pagination.totalItems"
        :pagination.sync="pagination"
        :rows-per-page-items="headerPageItems"
      >
        <v-progress-linear slot="progress" color="accent" indeterminate />
        <template slot="items" slot-scope="props">
          <tr>
            <td
              @click="
                props.expanded = !props.expanded
                setItem(props.item)
              "
              class="text-xs-left"
            >
              <div class="body-1 font-weight-bold">{{ props.item.name }}</div>
            </td>
            <td
              @click="
                props.expanded = !props.expanded
                setItem(props.item)
              "
              class="text-xs-left"
            >
              <div class="body-1 font-weight-bold">
                {{ props.item.description }}
              </div>
            </td>
            <td
              @click="
                props.expanded = !props.expanded
                setItem(props.item)
              "
              class="text-xs-left"
            >
              <div class="body-1 font-weight-bold">
                {{ props.item.changedByHoneyUser }}
              </div>
            </td>
            <td
              @click="
                props.expanded = !props.expanded
                setItem(props.item)
              "
              class="text-xs-left"
            >
              <div class="body-1 font-weight-bold">
                {{ props.item.revisionDateLocal }}
              </div>
            </td>
            <td
              @click="
                props.expanded = !props.expanded
                setItem(props.item)
              "
              class="text-xs-left"
            >
              <div class="body-1 font-weight-bold">
                {{ props.item.revisionTypeLocal }}
              </div>
            </td>
            <td
              @click="
                props.expanded = !props.expanded
                setItem(props.item)
              "
              class="text-xs-left"
            >
              <div class="body-1 font-weight-bold">
                {{ props.item.changedProfileName }}
              </div>
            </td>
            <td
              @click="
                props.expanded = !props.expanded
                setItem(props.item)
              "
              class="text-xs-left"
            >
              <div class="body-1 font-weight-bold">
                {{ props.item.changeMethodLocal }}
              </div>
            </td>
            <td
              @click="
                props.expanded = !props.expanded
                setItem(props.item)
              "
              class="text-xs-left"
            >
              <v-btn
                flat
                icon
                v-if="props.item.hexId !== null"
                :to="{
                  name: 'rule-detail',
                  params: { hexId: props.item.hexId }
                }"
              >
                <v-icon medium>link</v-icon>
              </v-btn>
            </td>
          </tr>
        </template>
        <template slot="expand" slot-scope="props">
          <v-card light class="rule-widget__specs">
            <v-card-text>
              <div :class="{ 'rule-widget': true }">
                <rule-widget
                  :dmn-xml="props.item.data"
                  :extra-buttons="extraButtons"
                  ref="ruleWidget"
                >
                </rule-widget>
              </div>
            </v-card-text>
          </v-card>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'
import RuleWidget from '../common/RuleWidget'
import camundaProviderModule from 'dmn-js-properties-panel/lib/provider/camunda'
import drdAdapterModule from 'dmn-js-properties-panel/lib/adapter/drd'
import camundaModdleDescriptor from 'camunda-dmn-moddle/resources/camunda'
import { saveAs } from 'file-saver'

export default {
  name: 'RuleHistory',
  components: { RuleWidget },
  data() {
    return {
      taskLoader: false,
      ruleItems: [],
      currentPage: 0,
      sortBy: '',
      rowsPerPage: 0,
      descending: false,
      search: '',
      fromDateMenu: false,
      fromDate: moment(new Date())
        .subtract(1, 'M')
        .toISOString()
        .substr(0, 10),
      toDateMenu: false,
      toDate: moment(new Date())
        .add(1, 'd')
        .toISOString()
        .substr(0, 10),
      propertiesProviderModules: [camundaProviderModule, drdAdapterModule],
      moddleDescriptors: { camunda: camundaModdleDescriptor },
      pagination: {
        sortBy: 'revisionDate',
        rowsPerPage: 10,
        descending: true,
        totalItems: 0,
        page: 1
      },
      headerPageItems: [5, 10, 25],
      extraButtons: [
        {
          title: 'Download Diagram as XML',
          method: () => {
            this.saveXML()
          },
          icon: 'get_app'
        },
        {
          title: 'Download Diagram as SVG',
          method: () => {
            this.saveSVG()
          },
          icon: 'image'
        }
      ],
      headers: [
        {
          text: 'Name',
          align: 'left',
          value: 'name'
        },
        { text: 'Description', value: 'description' },
        { text: 'Changed By', value: 'changedByHoneyUser' },
        { text: 'Changed At', value: 'revisionDate' },
        { text: 'Change Type', value: 'changeMethod' },
        { text: 'Changed Profile Name', value: 'changedProfileName' },
        { text: 'Change Method', value: 'revisionType' },
        { text: 'Link', value: 'link' }
      ]
    }
  },
  methods: {
    searchTasks() {
      this.refresh()
    },
    clearSearch() {
      this.search = ''
      this.searchTasks()
    },
    async refresh() {
      let pageParams = ''
      pageParams = `&page=${this.pagination.page}`
      if (this.pagination.rowsPerPage !== -1) {
        pageParams =
          pageParams +
          `&size=${this.pagination.rowsPerPage}&
           &sort=${this.pagination.sortBy}${
            this.pagination.descending ? ',desc' : ''
          }`
      }
      let urlParams = `fromDate=${this.fromDate}&toDate=${this.toDate}&search=${this.search}&honeyUserHexId=&hexId=${pageParams}`

      await this.$store.dispatch('fetchRuleHistory', urlParams)
    },

    saveXML: function() {
      this.$refs.ruleWidget
        .getDmnCanvas()
        .saveXML({ format: true }, (err, xml) => {
          if (!err) {
            saveAs(
              new Blob([xml], { type: 'text/xml;charset=utf-8' }),
              'rule.dmn'
            )
          }
        })
    },
    saveSVG: function() {
      this.$refs.ruleWidget.getDmnCanvas().saveSVG((err, svg) => {
        if (!err) {
          saveAs(
            new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }),
            'rule.svg'
          )
        }
      })
    }
  },
  mounted() {
    this.pagination.totalItems = this.totalRules
  },
  watch: {
    pagination: {
      handler() {
        try {
          if (
            this.pagination.page !== this.currentPage ||
            this.pagination.rowsPerPage !== this.rowsPerPage ||
            this.pagination.sortBy !== this.sortBy ||
            this.pagination.descending !== this.descending
          ) {
            this.currentPage = this.pagination.page
            this.rowsPerPage = this.pagination.rowsPerPage
            this.sortBy = this.pagination.sortBy
            this.descending = this.pagination.descending
            this.refresh()
          }
          // eslint-disable-next-line no-empty
        } catch (error) {}
      },
      deep: true
    },
    ruleHistory() {
      this.ruleItems = this.ruleHistory.map(rule => {
        rule.revisionDateLocal = moment(rule.revisionDate)
          .utc()
          .local()
          .format('DD MMM YYYY hh:mm:ss a')
        if (rule.revisionType === 'ADD') {
          rule.revisionTypeLocal = 'Add/Create'
        } else if (rule.revisionType === 'MOD') {
          rule.revisionTypeLocal = 'Modify/Update'
        } else if (rule.revisionType === 'DEL') {
          rule.revisionTypeLocal = 'Delete'
        }
        if (rule.changeMethod === 'ADD') {
          rule.changeMethodLocal = 'Add/Create'
        } else if (rule.changeMethod === 'DEL') {
          rule.changeMethodLocal = 'Delete'
        }
        return rule
      })
    },
    totalRules() {
      this.pagination.totalItems = this.totalRules
    },
    fromDateMenu() {
      this.refresh()
    },
    toDateMenu() {
      this.refresh()
    }
  },
  computed: {
    ...mapState({
      totalRules: state => state.rules.totalRules,
      ruleHistory: state => state.rules.ruleHistory,
      honeyUsers: state => state.honeyUsers.honeyUsers
    }),
    ...mapGetters({
      can: 'can'
    }),
    computedFromDate() {
      return this.fromDate
        ? moment(this.fromDate).format('dddd, MMMM Do YYYY')
        : ''
    },
    computedToDate() {
      return this.toDate ? moment(this.toDate).format('dddd, MMMM Do YYYY') : ''
    }
  }
}
</script>

<style scoped>
.rule-widget__specs {
  background: #fafafa;
  border-radius: 5px;
}

.rule-widget {
  height: 275px;
}
</style>
